// KOL: ยอดวิว จำนวน KOL และโพสต์ในช่วงที่เลือก
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { brandCfgLink } from "@features/navigation/model/side-menus";
import { fmtCompact, fmtInt } from "@shared/lib/format";
import { fetchKolSummary, overviewKeys } from "../api/overview-api";
import { kolRefsOf } from "../lib/kol-refs";
import { OvKpi } from "../components/ov-kpi";
import { OvLoading, OvNote } from "../components/ov-states";

export function KolBlock({ brand, days }: { brand: Brand; days: number }) {
  const { data: campaigns, isPending: loadingCampaigns } = useCampaigns(brand.id);
  const refs = kolRefsOf(campaigns);
  const hasToken = !!wazzupToken();

  const { data: summary, isPending } = useQuery({
    queryKey: overviewKeys.kolSummary(refs, days),
    queryFn: () => fetchKolSummary(refs, days),
    enabled: hasToken && refs.length > 0,
  });

  if (loadingCampaigns) return <OvLoading />;
  if (!refs.length)
    return (
      <OvNote>
        {(campaigns ?? []).length ? (
          <>
            ยังไม่ได้ผูกรายงาน KOL — ผูกได้ที่{" "}
            <a href={brandCfgLink(brand)}>หน้าตั้งค่า → Campaigns</a>
          </>
        ) : (
          "ยังไม่มีแคมเปญ"
        )}
      </OvNote>
    );
  if (hasToken && isPending) return <OvLoading />;

  const label = days ? `${days} วัน` : "ทั้งหมด";
  let views = 0;
  let kols = 0;
  let posts = 0;
  for (const ref of refs) {
    const r = summary?.get(ref);
    if (!r) continue;
    views += r.total_views ?? 0;
    // เลือกช่วงเวลา = นับ KOL ที่ลงโพสต์ในช่วงนั้น ไม่ใช่ทั้ง roster
    kols += (days ? r.active_kol_count : r.kol_count) ?? 0;
    posts += r.post_count ?? 0;
  }

  return (
    <div className="ov-kpis">
      <OvKpi
        label={`Total views · ${label}`}
        value={summary ? fmtCompact(views) : "—"}
        sub={
          !summary
            ? "ต้องเข้าสู่ระบบก่อน"
            : // ไม่มีโพสต์ในช่วงที่เลือก → บอกให้รู้ว่าเป็นเพราะช่วงเวลา ไม่ใช่ดึงข้อมูลพัง
              posts === 0
              ? `ไม่มีโพสต์ในช่วง ${label} — ลองขยายช่วง`
              : `${fmtInt(posts)} โพสต์`
        }
      />
      <OvKpi
        label={`KOL ที่ลงโพสต์ · ${label}`}
        value={summary ? fmtInt(kols) : "—"}
        sub={summary ? `ผูกไว้ ${refs.length} รายงาน` : ""}
      />
    </div>
  );
}
